import type { Thread } from '../data-contract/chat';
import type { ThreadStatus } from './thread-status';

export type ThreadFilter = 'all' | 'needs-input' | 'running' | 'unread';

type ThreadFilterPredicate = (status: ThreadStatus, isUnread: boolean) => boolean;

const THREAD_FILTER_PREDICATES: Readonly<Record<ThreadFilter, ThreadFilterPredicate>> = {
    all: () => true,
    'needs-input': (status) => status === 'needs-input',
    running: (status) => status === 'running',
    unread: (_status, isUnread) => isUnread,
};

export const THREAD_FILTERS: readonly ThreadFilter[] = ['all', 'needs-input', 'running', 'unread'];

type ThreadFilterInput = {
    readonly filter: ThreadFilter;
    readonly getStatus: (thread: Thread) => ThreadStatus;
    readonly isUnread: (thread: Thread) => boolean;
    readonly threads: readonly Thread[];
};

/** Keeps the threads that match the filter, in the order the API returned them. */
export const filterThreads = ({ filter, getStatus, isUnread, threads }: ThreadFilterInput): readonly Thread[] => {
    const matches = THREAD_FILTER_PREDICATES[filter];

    return threads.filter((thread) => matches(getStatus(thread), isUnread(thread)));
};

/** Counts the threads per filter so the rail can label each chip and hide the empty ones. */
export const countThreadsByFilter = (
    input: Omit<ThreadFilterInput, 'filter'>,
): Readonly<Record<ThreadFilter, number>> => ({
    all: input.threads.length,
    'needs-input': filterThreads({ ...input, filter: 'needs-input' }).length,
    running: filterThreads({ ...input, filter: 'running' }).length,
    unread: filterThreads({ ...input, filter: 'unread' }).length,
});
